import React, { useState, useEffect } from 'react';
import { SOCIAL_LOGIN_CONFIG } from '../config/socialLogin';

const FacebookDebug = () => {
  const [debugInfo, setDebugInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load Facebook SDK if not already loaded
    if (window.FB) return;
    const id = 'facebook-jssdk';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.onload = () => {
      try {
        window.FB.init({
          appId: SOCIAL_LOGIN_CONFIG.FACEBOOK_APP_ID,
          cookie: true,
          xfbml: false,
          version: 'v16.0',
        });
      } catch (e) {
        console.warn('FB init failed', e);
      }
    };
    document.body.appendChild(script);
  }, []);

  const responseFacebook = (response) => {
    // console.log('=== FACEBOOK DEBUG RESPONSE ==='); // Disabled for security
    // console.log('Full response:', response);
    // console.log('Response type:', typeof response);
    // console.log('Response keys:', Object.keys(response));
    
    setDebugInfo({
      timestamp: new Date().toISOString(),
      hasAccessToken: !!(response && response.accessToken),
      hasEmail: !!(response && response.email),
      hasFirstName: !!(response && response.first_name),
      hasLastName: !!(response && response.last_name),
      hasUserID: !!(response && response.userID),
      hasPicture: !!(response && response.picture),
      fullResponse: response
    });

    if (!response.accessToken) {
      setError('No access token received');
      return;
    }

    // Check for missing fields
    const missingFields = [];
    if (!response.email) missingFields.push('email');
    if (!response.first_name) missingFields.push('first_name');
    if (!response.last_name) missingFields.push('last_name');
    if (!response.userID) missingFields.push('userID');

    if (missingFields.length > 0) {
      setError(`Missing fields: ${missingFields.join(', ')}`);
    } else {
      setError(null);
    }
  };

  const clearDebug = () => {
    setDebugInfo(null);
    setError(null);
  };

  const triggerFBLogin = () => {
    if (!window.FB) {
      setError('Facebook SDK not loaded yet. Try again in a moment.');
      return;
    }

    window.FB.login((loginResp) => {
      if (!loginResp || !loginResp.authResponse) {
        setError('No auth response from Facebook.');
        return;
      }

      const accessToken = loginResp.authResponse.accessToken;
      // Fetch user profile
      window.FB.api('/me', { fields: 'id,first_name,last_name,name,email,picture' }, (profile) => {
        if (!profile) {
          setError('Failed to fetch profile from Facebook.');
          return;
        }
        const composed = {
          accessToken,
          userID: profile.id,
          email: profile.email,
          first_name: profile.first_name,
          last_name: profile.last_name,
          name: profile.name,
          picture: profile.picture,
        };
        setError(null);
        responseFacebook(composed);
      });
    }, { scope: 'public_profile,email' });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>Facebook Login Debug Tool</h2>
      <p>Use this to test your Facebook login configuration and see what data is returned.</p>
      
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={triggerFBLogin}
          className="facebook-login-btn"
          style={{ padding: '10px 18px', background: '#1877F2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
        >
          Test Facebook Login
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#ffebee', 
          border: '1px solid #f44336', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {debugInfo && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f5f5f5', 
          border: '1px solid #ddd', 
          borderRadius: '4px'
        }}>
          <h3>Debug Information</h3>
          <p><strong>Timestamp:</strong> {debugInfo.timestamp}</p>
          <p><strong>Has Access Token:</strong> {debugInfo.hasAccessToken ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Has Email:</strong> {debugInfo.hasEmail ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Has First Name:</strong> {debugInfo.hasFirstName ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Has Last Name:</strong> {debugInfo.hasLastName ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Has User ID:</strong> {debugInfo.hasUserID ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Has Picture:</strong> {debugInfo.hasPicture ? '✅ Yes' : '❌ No'}</p>
          
          <details style={{ marginTop: '15px' }}>
            <summary><strong>Full Response Data</strong></summary>
            <pre style={{ 
              backgroundColor: '#fff', 
              padding: '10px', 
              border: '1px solid #ccc',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(debugInfo.fullResponse, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div style={{ marginTop: '20px' }}>
        <button 
          onClick={clearDebug}
          style={{
            padding: '10px 20px',
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Debug Info
        </button>
      </div>

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
        <h3>What to check if login fails:</h3>
        <ol>
          <li>Check browser console for detailed logs</li>
          <li>Verify your Facebook App ID is correct</li>
          <li>Make sure your app is in Development mode</li>
          <li>Add 'localhost' to App Domains in Facebook app settings</li>
          <li>Add 'http://localhost:3000/' to Valid OAuth Redirect URIs</li>
          <li>Check if 'email' permission is approved in App Review</li>
        </ol>
      </div>
    </div>
  );
};

export default FacebookDebug; 